/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { RequestsExportJobV1 } from '../models/requests-export-job-v-1';


/**
 * API for SLM reporting.
 */
@Injectable({
  providedIn: 'root',
})
export class ReportingV1Service extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation initiateApprovedRequestsExportV1
   */
  static readonly InitiateApprovedRequestsExportV1Path = '/v1/reporting/requests/approved/export';

  /**
   * Initiate an async export of approved requests.
   *
   * Creates an async export job for approved requests (all forms) within the given completed-date range. Returns a job ID and status URL to poll.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `initiateApprovedRequestsExportV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  initiateApprovedRequestsExportV1$Response(params: {
    from: string;
    to: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RequestsExportJobV1>> {

    const rb = new RequestBuilder(this.rootUrl, ReportingV1Service.InitiateApprovedRequestsExportV1Path, 'post');
    if (params) {
      rb.query('from', params.from, {});
      rb.query('to', params.to, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RequestsExportJobV1>;
      })
    );
  }

  /**
   * Initiate an async export of approved requests.
   *
   * Creates an async export job for approved requests (all forms) within the given completed-date range. Returns a job ID and status URL to poll.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `initiateApprovedRequestsExportV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  initiateApprovedRequestsExportV1(params: {
    from: string;
    to: string;
  },
  context?: HttpContext

): Observable<RequestsExportJobV1> {

    return this.initiateApprovedRequestsExportV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<RequestsExportJobV1>) => r.body as RequestsExportJobV1)
    );
  }

  /**
   * Path part for operation getReportingRequestsByStatusV1
   */
  static readonly GetReportingRequestsByStatusV1Path = '/v1/reporting/requests/by-status';

  /**
   * Get request counts by status for a date range.
   *
   * Per-form, per-status request counts and trends vs the prior equivalent period. One entry per form that had activity in the selected range. Statuses per form: Draft, Requires review (internal), Pending supplier action, Requires review (external), Approved. A request that moved between statuses is counted once per status. Date range is inclusive on both ends (UTC day boundaries).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getReportingRequestsByStatusV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReportingRequestsByStatusV1$Response(params: {
    from: string;
    to: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ReportingV1Service.GetReportingRequestsByStatusV1Path, 'get');
    if (params) {
      rb.query('from', params.from, {});
      rb.query('to', params.to, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Get request counts by status for a date range.
   *
   * Per-form, per-status request counts and trends vs the prior equivalent period. One entry per form that had activity in the selected range. Statuses per form: Draft, Requires review (internal), Pending supplier action, Requires review (external), Approved. A request that moved between statuses is counted once per status. Date range is inclusive on both ends (UTC day boundaries).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getReportingRequestsByStatusV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReportingRequestsByStatusV1(params: {
    from: string;
    to: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.getReportingRequestsByStatusV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation exportReportingRequestsByStatusV1
   */
  static readonly ExportReportingRequestsByStatusV1Path = '/v1/reporting/requests/by-status/export';

  /**
   * Export requests by status as CSV.
   *
   * Columns: Status, Count, Trend. Pass formId to export a single form's widget; omit to export all forms.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `exportReportingRequestsByStatusV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportReportingRequestsByStatusV1$Response(params: {
    from: string;
    to: string;
    formId?: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ReportingV1Service.ExportReportingRequestsByStatusV1Path, 'get');
    if (params) {
      rb.query('from', params.from, {});
      rb.query('to', params.to, {});
      rb.query('formId', params.formId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Export requests by status as CSV.
   *
   * Columns: Status, Count, Trend. Pass formId to export a single form's widget; omit to export all forms.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `exportReportingRequestsByStatusV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportReportingRequestsByStatusV1(params: {
    from: string;
    to: string;
    formId?: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.exportReportingRequestsByStatusV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getApprovedRequestsExportStatusV1
   */
  static readonly GetApprovedRequestsExportStatusV1Path = '/v1/reporting/requests/approved/export/{jobId}';

  /**
   * Get approved requests export job status.
   *
   * Returns the current status of an approved requests export job. When status is COMPLETED the response contains a pre-signed download URL.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getApprovedRequestsExportStatusV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  getApprovedRequestsExportStatusV1$Response(params: {
    jobId: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RequestsExportJobV1>> {

    const rb = new RequestBuilder(this.rootUrl, ReportingV1Service.GetApprovedRequestsExportStatusV1Path, 'get');
    if (params) {
      rb.path('jobId', params.jobId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RequestsExportJobV1>;
      })
    );
  }

  /**
   * Get approved requests export job status.
   *
   * Returns the current status of an approved requests export job. When status is COMPLETED the response contains a pre-signed download URL.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getApprovedRequestsExportStatusV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getApprovedRequestsExportStatusV1(params: {
    jobId: string;
  },
  context?: HttpContext

): Observable<RequestsExportJobV1> {

    return this.getApprovedRequestsExportStatusV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<RequestsExportJobV1>) => r.body as RequestsExportJobV1)
    );
  }

  /**
   * Path part for operation getReportingPipelineV1
   */
  static readonly GetReportingPipelineV1Path = '/v1/reporting/pipeline';

  /**
   * Get live request counts broken down by open status.
   *
   * Returns a count per status and the total open count (excludes APPROVED and CANCELLED). All statuses are returned — the frontend decides which tiles to display. Suitable for independent section loading with skeleton screens.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getReportingPipelineV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReportingPipelineV1$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ReportingV1Service.GetReportingPipelineV1Path, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Get live request counts broken down by open status.
   *
   * Returns a count per status and the total open count (excludes APPROVED and CANCELLED). All statuses are returned — the frontend decides which tiles to display. Suitable for independent section loading with skeleton screens.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getReportingPipelineV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReportingPipelineV1(params?: {
  },
  context?: HttpContext

): Observable<void> {

    return this.getReportingPipelineV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getReportingPipelineRequestsV1
   */
  static readonly GetReportingPipelineRequestsV1Path = '/v1/reporting/pipeline/{status}/requests';

  /**
   * Get the 5 most recently updated requests for a given status.
   *
   * Returns up to 5 requests in the given status ordered by lastModifiedByDate descending. Status must be one of the status enum names (e.g. DRAFT, REQUIRES_REVIEW_INTERNAL).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getReportingPipelineRequestsV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReportingPipelineRequestsV1$Response(params: {
    status: 'Draft' | 'Requires review (internal)' | 'Returned to requestor' | 'Pending supplier action' | 'Requires review (external)' | 'Returned to supplier' | 'Approved' | 'ERP synced' | 'ERP sync failed' | 'Cancelled';
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ReportingV1Service.GetReportingPipelineRequestsV1Path, 'get');
    if (params) {
      rb.path('status', params.status, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Get the 5 most recently updated requests for a given status.
   *
   * Returns up to 5 requests in the given status ordered by lastModifiedByDate descending. Status must be one of the status enum names (e.g. DRAFT, REQUIRES_REVIEW_INTERNAL).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getReportingPipelineRequestsV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReportingPipelineRequestsV1(params: {
    status: 'Draft' | 'Requires review (internal)' | 'Returned to requestor' | 'Pending supplier action' | 'Requires review (external)' | 'Returned to supplier' | 'Approved' | 'ERP synced' | 'ERP sync failed' | 'Cancelled';
  },
  context?: HttpContext

): Observable<void> {

    return this.getReportingPipelineRequestsV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation exportAllReportingV1
   */
  static readonly ExportAllReportingV1Path = '/v1/reporting/export/all';

  /**
   * Export all reporting widgets as a single CSV.
   *
   * Exports all form widgets followed by the approver statistics widget in one file. Form sections use the currently selected date range.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `exportAllReportingV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportAllReportingV1$Response(params: {
    from: string;
    to: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ReportingV1Service.ExportAllReportingV1Path, 'get');
    if (params) {
      rb.query('from', params.from, {});
      rb.query('to', params.to, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Export all reporting widgets as a single CSV.
   *
   * Exports all form widgets followed by the approver statistics widget in one file. Form sections use the currently selected date range.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `exportAllReportingV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportAllReportingV1(params: {
    from: string;
    to: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.exportAllReportingV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getReportingApproverStatsV1
   */
  static readonly GetReportingApproverStatsV1Path = '/v1/reporting/approvers';

  /**
   * Get approver statistics for a date range.
   *
   * Per-approver completed/outstanding section counts and average response time in business days. Includes deactivated users active in the period. SUPERSEDED approvals are excluded. Date range is inclusive on both ends (UTC day boundaries).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getReportingApproverStatsV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReportingApproverStatsV1$Response(params: {
    from: string;
    to: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ReportingV1Service.GetReportingApproverStatsV1Path, 'get');
    if (params) {
      rb.query('from', params.from, {});
      rb.query('to', params.to, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Get approver statistics for a date range.
   *
   * Per-approver completed/outstanding section counts and average response time in business days. Includes deactivated users active in the period. SUPERSEDED approvals are excluded. Date range is inclusive on both ends (UTC day boundaries).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getReportingApproverStatsV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReportingApproverStatsV1(params: {
    from: string;
    to: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.getReportingApproverStatsV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation exportReportingApproverStatsV1
   */
  static readonly ExportReportingApproverStatsV1Path = '/v1/reporting/approvers/export';

  /**
   * Export approver statistics as CSV.
   *
   * Columns: Approver, Completed, Outstanding, Average Time to Review.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `exportReportingApproverStatsV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportReportingApproverStatsV1$Response(params: {
    from: string;
    to: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ReportingV1Service.ExportReportingApproverStatsV1Path, 'get');
    if (params) {
      rb.query('from', params.from, {});
      rb.query('to', params.to, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Export approver statistics as CSV.
   *
   * Columns: Approver, Completed, Outstanding, Average Time to Review.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `exportReportingApproverStatsV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportReportingApproverStatsV1(params: {
    from: string;
    to: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.exportReportingApproverStatsV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

}
